﻿import Tooltip from "@mui/material/Tooltip";

import { baseCrumbClasses } from "../Breadcrumbs";
import { DoubleShift, TripleShift } from "../../../types/Shift";
import { ListSelectCrumb } from "../ListSelectCrumb";
import useShiftTimes from "../../Hooks/useShiftTimes";
import useShiftLockInfo from "../../Hooks/useShiftLockInfo";
import { isShiftInFuture } from "../../../utilities/helpers/shiftLockStateHelpers";

// Note: one shape, TripleShift-typed, rather than a discriminated union on showAfternoonShift. The
// selected shift comes from redux, which does carry AFTERNOON, so the narrow branch could not accept it
// - and the flag only decides whether afternoon is *offered*, not what the caller is able to hold. The
// DoubleShift interface was also declared twice, which TypeScript silently merged.
interface ShiftSelectCrumbProps {
  selectedShift: TripleShift;
  setSelectedShift: (shift: TripleShift) => void;
  showAfternoonShift?: boolean;
}

const isNight = (shift: DoubleShift | TripleShift) => shift === "NIGHT";
const shiftIcon = (shift: DoubleShift | TripleShift) => isNight(shift) ? "🌙" : "☀️";
const shiftLabel = (shift: DoubleShift | TripleShift) =>
  `${shift.charAt(0).toUpperCase()}${shift.slice(1).toLowerCase()} Shift`;

const NIGHT_NOT_STARTED_TOOLTIP = "Night shift has not started yet.";

export const ShiftSelectCrumb = (props: ShiftSelectCrumbProps) => {
  const { selectedShift, setSelectedShift, showAfternoonShift } = props;
  const baseClass = `${baseCrumbClasses} flex items-center mx-0.5 justify-center gap-1 text-sm md:text-base transition-colors w-[165px] short:w-[120px]`;

  const { selectedDate } = useShiftTimes();
  const { shiftLockState } = useShiftLockInfo(selectedDate);
  const nightInFuture = isShiftInFuture(selectedDate, "N", shiftLockState);

  if (showAfternoonShift) {
    const shifts: TripleShift[] = ["DAY", "AFTERNOON", "NIGHT"];

    return (
      <ListSelectCrumb<TripleShift>
        options={shifts}
        selectedOption={selectedShift}
        setSelectedOption={s => setSelectedShift(s!)}
        getOptionValue={s => ({ DAY: 1, AFTERNOON: 2, NIGHT: 3 }[s])}
        getOptionLabel={shiftLabel}
        getOptionDisabled={s => s === "NIGHT" && nightInFuture}
        className={`${baseClass} ${isNight(selectedShift) ? "bg-[#2d2964] border border-[#737f90] hover:bg-[#4d4a88] text-[#e0e7ff]" : "bg-[#ea580c] border border-[#e5e7eb] hover:bg-[#c2410c] text-[#f9f9f9]"}`}
        renderSelected={s => (
          <>
            <span>{shiftIcon(s)}</span>
            <span>{shiftLabel(s)}</span>
          </>
        )}
      />
    );
  }

  const nightUnreachable = nightInFuture && selectedShift !== "NIGHT";
  const handleClick = () => {
    if (nightUnreachable) return;
    setSelectedShift(selectedShift === "DAY" ? "NIGHT" : "DAY");
  };

  const button = (
    <button
      className={`${baseClass} disabled:opacity-50 disabled:cursor-not-allowed ${isNight(selectedShift) ? "bg-[#2d2964] border border-[#737f90] hover:bg-[#4d4a88] text-[#e0e7ff]" : "bg-[#ea580c] border border-[#e5e7eb] hover:bg-[#c2410c] text-[#f9f9f9]"}`}
      onClick={handleClick}
      disabled={nightUnreachable}
    >
      <span>{shiftIcon(selectedShift)}</span>
      <span>{shiftLabel(selectedShift)}</span>
    </button>
  );

  return nightUnreachable
    ? <Tooltip title={NIGHT_NOT_STARTED_TOOLTIP} arrow placement="top"><span className="inline-flex">{button}</span></Tooltip>
    : button;
};